import { useTranslation } from "react-i18next";
import Button from "./Button";
import { IoArrowRedoSharp, IoLogoGithub, IoArrowForwardOutline } from "react-icons/io5";
import TrackVisibility from "react-on-screen";
import { Link } from "react-router-dom";

interface Project {
    imageSrc?: string;
    name: string;
    subtitle?: string;
    link?: string;
    githubLink?: string;
    internalLink?: string;
    date: string;
    featured?: boolean;
}

const projects: Project[] = [
    {
        imageSrc: "holareparto.webp",
        name: "Hola Reparto",
        subtitle: "HolaRepartoSubtitle",
        internalLink: "/holareparto",
        date: "2026",
        featured: true
    },
    {
        imageSrc: "tekkura.webp",
        name: "Tekkura",
        subtitle: "TekkuraSubtitle",
        link: "https://tekkura.app/",
        date: "2026"
    },
    {
        imageSrc: "swapvid.webp",
        name: "SwapVid",
        subtitle: "SwapVidSubtitle",
        githubLink: "https://github.com/curiux/swapvid",
        link: "https://swapvid.vercel.app/",
        date: "2025"
    },
    {
        imageSrc: "gleamgenius.webp",
        name: "GleamGenius",
        link: "https://gleamgenius.vercel.app/",
        date: "2024"
    }
];

const otherProjects: Project[] = [
    {
        imageSrc: "airtrip.webp",
        name: "Airtrip",
        link: "https://airtrip.vercel.app/",
        date: "2024"
    },
    {
        imageSrc: "grix-translator.webp",
        name: "Grix Translator",
        link: "https://grix-five.vercel.app/",
        date: "2023"
    }
];

interface ProjectProps {
    project: Project;
    isVisible: boolean;
}

function ProjectCard({ project, isVisible }: ProjectProps) {
    const { t } = useTranslation();

    return (
        <div className={`${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"} w-full h-full shadow-lg rounded-xl transition-all duration-700 flex flex-col overflow-hidden bg-white hover:shadow-2xl`}>
            {project.imageSrc && (
                <div className="relative w-full aspect-[1919/890] border-b border-darkBlue/5 shrink-0">
                    <img
                        src={`projects/${project.imageSrc}`}
                        alt={project.name}
                        className="w-full h-full object-cover"
                    />
                    {project.featured && (
                        <span className="absolute top-2 left-2 cursor-default select-none rounded-full bg-gradient-to-br from-white via-cyan to-cyan/80 px-2 py-0.5 text-[7px] md:top-2.5 md:left-2.5 md:px-2.5 md:py-1 md:text-[10px] font-bold uppercase tracking-widest text-darkBlue shadow-[0_0_18px_rgba(34,211,238,0.85)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_24px_rgba(34,211,238,1)]">
                            {t("featuredProject")}
                        </span>
                    )}
                </div>
            )}
            <div className="p-5 md:p-6 flex flex-col flex-grow">
                <div className="mb-4">
                    <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-lg md:text-xl text-darkBlue leading-tight">{project.name}</h3>
                        <span className="text-[10px] md:text-xs text-darkBlue opacity-40 font-semibold uppercase tracking-wider">— {project.date}</span>
                    </div>
                    {project.subtitle && (
                        <p className="text-[10px] md:text-xs font-semibold text-slate/80 leading-tight">
                            {t(project.subtitle)}
                        </p>
                    )}
                </div>

                <p className="text-xs md:text-sm text-darkBlue opacity-75 leading-relaxed flex-grow mb-6">
                    {t(project.name.replaceAll(" ", ""))}
                </p>

                <div className="flex flex-wrap gap-3 items-center mt-auto">
                    {project.githubLink && (
                        <a href={project.githubLink} target="_blank" rel="noopener noreferrer" className="p-2 -m-2 text-darkBlue hover:text-cyan transition-colors" title="GitHub Repository">
                            <IoLogoGithub size={"1.3em"} />
                        </a>
                    )}
                    {project.link && (
                        <Button class="py-1.5 px-3 border border-darkBlue/20 hover:bg-cyan hover:border-cyan hover:text-darkBlue text-[10px] md:text-xs font-bold flex items-center gap-1.5 transition-all duration-300 shadow-sm" link={project.link}>
                            {t("visitBtn").toUpperCase()}
                            <IoArrowRedoSharp size={"0.8em"} />
                        </Button>
                    )}
                    {project.internalLink && (
                        <Link to={project.internalLink} className="py-1.5 px-3 bg-cyan border border-cyan hover:bg-darkBlue hover:border-darkBlue hover:text-white text-darkBlue text-[10px] md:text-xs font-bold flex items-center gap-1.5 shadow-sm rounded-md transition duration-300">
                            {project.featured ? t("viewCaseStudy").toUpperCase() : t("moreBtn").toUpperCase()}
                            <IoArrowForwardOutline size={"0.9em"} />
                        </Link>
                    )}
                </div>
            </div>
        </div>
    )
}

function ProjectGrid({ items }: { items: Project[] }) {
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 w-full max-w-6xl">
            {items.map(project => (
                <TrackVisibility key={project.name} offset={100} partialVisibility className="h-full">
                    {({ isVisible }) => (
                        <ProjectCard project={project} isVisible={isVisible} />
                    )}
                </TrackVisibility>
            ))}
        </div>
    );
}

export default function Projects() {
    const { t } = useTranslation();

    return (
        <div className="flex flex-col items-center w-full px-4">
            <ProjectGrid items={projects} />
            <h3 className="font-bold text-darkBlue opacity-60 uppercase tracking-widest text-xs mt-16 mb-6 md:text-sm">
                {t("otherProjects")}
            </h3>
            <ProjectGrid items={otherProjects} />
        </div>
    );
}
